import React from 'react';

import type { GeneralEvent } from '@vlabs/api/luna-api/model';
import { FIRE_CLASS_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import {
  BalaklavaIcon,
  BriefcaseIcon,
  FallIcon,
  FightIcon,
  FireIcon,
  GunIcon,
  HandsUpIcon,
  LyingPersonIcon,
  PeopleIcon,
} from '@vlabs/icons';
import { usePeopleCountThresholdInfo } from '@vlabs/shared/hooks';
import { Tag, useWindowSize } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectFacecoverVersion } from '@vlapps/point/src/app/store/selectors';

import { AnalyticTag } from './AnalyticTag';
import { BaseGeneralEventPreview } from './BaseGeneralEventPreview';
import st from './BaseGeneralEventPreview.module.sass';

interface EventPreviewProps {
  event: GeneralEvent;
  onClick?: (params: { event: GeneralEvent }) => void;
}
export function EventPreviewCrowd({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const { label } = usePeopleCountThresholdInfo(event);

  const crowdThresholdLabel = label ? `(${label})` : '';
  const peopleCount = event?.event?.people_count;
  const tagLabel = `${peopleCount} ${t('videoAnalytics:crowdAnalyticCard.circle bar.label')} ${crowdThresholdLabel}`;

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<PeopleIcon />}
          isAlarm={isAlarm}
          label={tagLabel}
        />
      )}
    />
  );
}

export function EventPreviewFight({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<FightIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:fightAnalyticCard.badge.label')}
        />
      )}
    />
  );
}

export function EventPreviewFire({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  const fireClassLabel = FIRE_CLASS_OPTIONS?.[event?.event?.fire_class]?.label;

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <React.Fragment>
          <AnalyticTag
            icon={<FireIcon className={st.PulsingIcon} />}
            isAlarm={isAlarm}
            isAlwaysAlarm
            label={t('videoAnalytics:fireAnalyticCard.badge.label')}
          />
          {!!fireClassLabel && (
            <Tag
              className={st.Tag}
              data-testid="workspace.analyticEventCard.extraTagType"
              kind="neutral"
              size={windowSize.width < 1920 ? 'medium' : 'large'}
              value={fireClassLabel}
            />
          )}
        </React.Fragment>
      )}
    />
  );
}

export function EventPreviewWeapon({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<GunIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:weaponAnalyticCard.badge.label')}
        />
      )}
    />
  );
}

export function EventPreviewFacecover({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const windowSize = useWindowSize();
  const { t } = useTranslation();

  const facecoverVersion = useSelector(selectFacecoverVersion);

  const eventType = event?.event_type;
  const isV2 = facecoverVersion === 'v2';
  const subtype = isV2 && eventType !== 'facecover' ? eventType : null;

  const label = isV2
    ? t('videoAnalytics:facecoverAnalyticCardV2.badge.label')
    : t('videoAnalytics:facecoverAnalyticCard.badge.label');
  const subtypeLabel = t(`videoAnalytics:facecoverAnalyticDetailsV2.subtypes.${subtype}`, {
    defaultValue: subtype,
  });

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <React.Fragment>
          <AnalyticTag
            icon={<BalaklavaIcon className={st.PulsingIcon} />}
            isAlarm={isAlarm}
            isAlwaysAlarm
            label={label}
          />
          {!!subtype && (
            <Tag
              className={st.Tag}
              data-testid="workspace.analyticEventCard.extraTagType"
              kind="neutral"
              size={windowSize.width < 1920 ? 'medium' : 'large'}
              value={subtypeLabel}
            />
          )}
        </React.Fragment>
      )}
    />
  );
}

export function EventPreviewBag({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<BriefcaseIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:event_type.bag.label')}
        />
      )}
    />
  );
}

export function EventPreviewLying({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<LyingPersonIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:lyingAnalyticCard.badge.label')}
        />
      )}
    />
  );
}

export function EventPreviewHandsup({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<HandsUpIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:handsupAnalyticCard.badge.label')}
        />
      )}
    />
  );
}

export function EventPreviewFall({ event, onClick = undefined }: EventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <BaseGeneralEventPreview
      event={event}
      onClick={onClick}
      renderTags={({ isAlarm }) => (
        <AnalyticTag
          icon={<FallIcon className={st.PulsingIcon} />}
          isAlarm={isAlarm}
          label={t('videoAnalytics:fallAnalyticCard.badge.label')}
        />
      )}
    />
  );
}
